import { useCallback, useContext, useMemo } from "react";
import { useAppDispatch, useAppSelector } from ".";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { get } from "lodash";
import { PLANS } from "@/constants/plans";
import { FeatureKey } from "@/types/plans";
import { openUpgradePopUp as openUpgradePopUpAction, closeUpgradePopUp as closeUpgradePopUpAction } from '@/store/uiSlice';
import useTerminology from "./useTerminology";

const useOrganizationPlan = (feature?: FeatureKey) => {
    const dispatch = useAppDispatch();
    const { t } = useTerminology();
    const websiteContext = useContext(WebsiteContext);
    const activeOrganizationId = useAppSelector((store) => store.ui.activeOrganization);
    const storeOrganization = useAppSelector((state) => state?.organizations[activeOrganizationId ?? ""] ?? null);

    const activeOrganization = useMemo(() => {
        return websiteContext?.organization || storeOrganization || null;
    }, [storeOrganization, websiteContext?.organization]);

    const checkFeatureLimit = useCallback((usedQuota: number | boolean) => {
        if (!activeOrganization?.plan) return false;
    
        const planLimit = get(PLANS, [activeOrganization.plan, feature, 'limit'], null);
    
        if (!planLimit || typeof planLimit !== 'number' || typeof usedQuota !== 'number') return false;
    
        if (usedQuota < planLimit) return true;
    
        return false;
    }, [activeOrganization]);

    const getFeatureQuota = useCallback(() => {
        if (!activeOrganization?.plan) return null;
        
        const planLimit = get(PLANS, [activeOrganization?.plan, feature, 'limit'], null);
        return planLimit;
    }, [activeOrganization]);

    const openUpgradePopUp = useCallback((customMessage?: string) => {
        if (!activeOrganization?.plan) return null;
        let message = get(PLANS, [activeOrganization?.plan, feature, 'message'], 'Para continuar actualiza tu plan');

        if (feature === 'content') {
            if (activeOrganization.plan === 'free') {
                message = t('product') === 'plato'
                    ? 'Alcanzaste el límite de creación de platos y bebidas'
                    : 'Alcanzaste el límite de creación de productos';
            } else if (activeOrganization.plan === 'pro') {
                message = `Tu plan Pro permite crear hasta 100 ${t('product', 2)}.`;
            }
        } else if (feature === 'menu') {
            if (activeOrganization.plan === 'free') {
                message = `Tu plan permite crear 1 ${t('catalog')}.`;
            } else if (activeOrganization.plan === 'pro') {
                message = `Alcanzaste el límite de creación de ${t('catalog', 2)}`;
            }
        } else if (feature === 'survey') {
            if (activeOrganization.plan === 'free') {
                message = 'Tu plan gratuito permite crear hasta 2 encuestas.';
            } else if (activeOrganization.plan === 'pro') {
                message = 'Alcanzaste el límite de creación de encuestas.';
            }
        }

        dispatch(openUpgradePopUpAction(customMessage || message));
    }, [activeOrganization, feature, t]);
    
    const closeUpgradePopUp = useCallback(() => {
        dispatch(closeUpgradePopUpAction());
    }, []);

    const isFeatureAvailable = useCallback((subFeature: string) => {
        if (!activeOrganization?.plan || !subFeature) return false;
        
        const featureSettings = get(PLANS, [activeOrganization.plan, feature], null);
        
        if (!featureSettings) return false;
        
        const features = get(featureSettings, 'features', []);
        return features.includes(subFeature);
    }, [activeOrganization, feature]);
    
    const isEnabled = useMemo(() => {
        if (!activeOrganization?.plan || !feature) return false;
        return get(PLANS, [activeOrganization.plan, feature, "isEnabled"], false);
    }, [activeOrganization, feature]);

    return {
        getFeatureQuota,
        openUpgradePopUp,
        closeUpgradePopUp,
        checkFeatureLimit,
        isFeatureAvailable,
        isEnabled,
    }
}

export default useOrganizationPlan;
